import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useRouter } from '@tanstack/react-router';
import { formatDate, type PublicReport, type ResolvedData } from '@tallyframe/core';
import { ReportViewer } from '@tallyframe/renderer';
import { usePublicReport } from '../api/public-queries';
import { rememberReturnPath } from '../auth/return-path';
import { RemixDialog } from '../reports/RemixDialog';
import { Button, ButtonLink } from '../ui/Button';
import { StatusState } from '../ui/StatusState';

const BADGE_URL = 'https://reportpad.app/?ref=published';

/** The public payload carries only what the viewer reads; the ids the renderer's
 *  types ask for are placeholders that never leave this page. */
function toResolvedData(report: PublicReport): ResolvedData {
  const asOf = report.snapshotMeta.dataAsOf ?? report.publishedAt;
  return {
    dataset: {
      id: 'published',
      name: report.dataset.name,
      grain: report.dataset.grain,
      schemaVersion: 1,
      createdAt: asOf,
      columns: report.dataset.columns,
      ...(report.dataset.uniqueKey ? { uniqueKey: report.dataset.uniqueKey } : {})
    },
    snapshot: {
      id: 'published',
      datasetId: 'published',
      schemaFingerprint: '',
      schemaVersion: 1,
      receivedAt: asOf,
      rowCount: report.snapshotMeta.rowCount
    },
    rows: report.rows
  };
}

/** The public page at /p/:id: a read-only report under a slim bar. */
export function PublishedRoute(props: {
  linkId: string;
  signedIn: boolean;
  activeWorkspaceId: string | null;
}): ReactElement {
  const report = usePublicReport(props.linkId);
  const router = useRouter();
  const [dialogOpen, setDialogOpen] = useState(
    () => props.signedIn && new URLSearchParams(router.state.location.searchStr).get('remix') === '1'
  );
  const ready = report.data ?? null;
  const data = useMemo(() => (ready ? toResolvedData(ready) : null), [ready]);

  // Whatever went wrong, the viewer is told only that the link is unavailable.
  if (report.isPending) {
    return (
      <main className="m-0 p-0">
        <StatusState kind="loading" title="Loading…" page />
      </main>
    );
  }
  if (!ready || !data) {
    return (
      <main className="m-0 p-0">
        <StatusState kind="empty" title="This link isn't available" page />
      </main>
    );
  }

  function onRemix(): void {
    if (props.signedIn) {
      setDialogOpen(true);
      return;
    }
    rememberReturnPath(`/p/${props.linkId}?remix=1`);
    void router.navigate({ to: '/login' });
  }

  function onDialogOpenChange(open: boolean): void {
    setDialogOpen(open);
    if (!open && new URLSearchParams(router.state.location.searchStr).has('remix')) {
      void router.navigate({ href: `/p/${props.linkId}`, replace: true });
    }
  }

  return (
    <main className="min-h-screen m-0 p-0 bg-surface text-ink font-ui">
      {/* Wraps on phones: the name and date on one line, the actions under them. */}
      <div className="m-0 flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2">
        <strong className="min-w-0 truncate">{ready.brand.name}</strong>
        <span className="text-sm text-muted">
          Data as of {formatDate(ready.snapshotMeta.dataAsOf ?? ready.publishedAt)}
        </span>
        <span className="flex-1" />
        {ready.dataset.remixable ? (
          <Button variant="primary" size="sm" onPress={onRemix}>
            Remix
          </Button>
        ) : null}
        {ready.badge ? (
          <ButtonLink variant="quiet" size="sm" href={BADGE_URL} target="_blank" rel="noopener noreferrer">
            Made with ReportPad
          </ButtonLink>
        ) : null}
      </div>
      <div className="m-0 p-0">
        <ReportViewer definition={ready.definition} data={data} brand={ready.brand} />
      </div>
      {ready.dataset.remixable ? (
        <RemixDialog
          linkId={props.linkId}
          activeWorkspaceId={props.activeWorkspaceId}
          isOpen={dialogOpen}
          onOpenChange={onDialogOpenChange}
        />
      ) : null}
    </main>
  );
}
